/**
 * OrgScope serialization helpers.
 *
 * Story: org-006-1 (unified proposal list — first user-visible piece of
 * ORG Phase 1; consumes the canister-side `OrgScope` filter shipped by
 * org-002-3 via the oracle-bridge HTTP layer added in this story's
 * oracle-bridge PR).
 *
 * `OrgScope` is the canonical TS mirror of the governance canister's Candid
 * variant. R8 invariant — keep the explicit three-arm discriminated union
 * (do NOT collapse to `Option<OrgId>` or to a string enum). Both ends of the
 * wire — canister + bridge transformer — depend on this shape.
 *
 * This module exports a JSON-safe serializer (`bigint` → decimal string) +
 * the HTTP-query-param wire encoding that the bridge route expects.
 */
import type { OrgScope } from '@/stores/types';

/**
 * JSON-safe wire shape — `bigint` arm stringified to decimal so the value
 * can flow through `JSON.stringify` (which throws on raw bigint) into
 * `useProposalList`'s `paramsKey` and any future URL-state-sync that
 * org-006-2 adds.
 *
 * The deserialization side is symmetric — bigint reconstituted from the
 * string when reading the value back. Not used internally today but the
 * pair is the right shape for forward-compat.
 */
export type SerializedOrgScope =
  | { All: null }
  | { HelloWorld: null }
  | { Org: string };

/**
 * Serialize an `OrgScope` to a JSON-safe shape. The `Org` arm's `bigint`
 * is converted to a decimal string (URL query params are strings anyway,
 * and `JSON.stringify` cannot serialize a raw `bigint`).
 *
 * Pass `undefined` through — callers that never set an `orgScope` get
 * `undefined` back, which `JSON.stringify` happily encodes as the literal
 * `undefined` (i.e. omitted from the resulting JSON).
 *
 * Semantic identity: `{ All: null }` collapses to `undefined` so a parent
 * toggling between the two forms doesn't burn an unnecessary refetch in
 * `useProposalList`'s `paramsKey` comparison (both forms send NO
 * `org_scope` HTTP query param — they're indistinguishable at the wire).
 */
export function serializeOrgScope(scope: OrgScope | undefined): SerializedOrgScope | undefined {
  if (scope === undefined) return undefined;
  if ('All' in scope) return undefined; // semantic identity with omission
  if ('HelloWorld' in scope) return { HelloWorld: null };
  return { Org: scope.Org.toString() };
}

/**
 * Inverse of `serializeOrgScope` — reconstitute an `OrgScope` from the
 * JSON-safe form (decimal string → bigint).
 *
 * Returns `undefined` for `undefined` input. Throws on a malformed
 * `{ Org }` string (non-numeric); upstream URL-state-sync code (org-006-2)
 * will need to guard against that and degrade to `{ All: null }`.
 */
export function deserializeOrgScope(
  serialized: SerializedOrgScope | undefined
): OrgScope | undefined {
  if (serialized === undefined) return undefined;
  if ('All' in serialized) return { All: null };
  if ('HelloWorld' in serialized) return { HelloWorld: null };
  return { Org: BigInt(serialized.Org) };
}

/**
 * HTTP query-param wire encoding for the oracle-bridge `GET /api/governance/proposals`
 * route (see `oracle-bridge/src/routes/governance-proposals.ts` org-006-1 wiring).
 *
 *   - `undefined`           → no params (semantic All — wire-back-compatible
 *                             with the pre-org-002-3 oracle-bridge that ignores
 *                             unknown query params)
 *   - `{ All: null }`       → no params (semantic identity with undefined)
 *   - `{ HelloWorld: null }` → `org_scope=helloworld`
 *   - `{ Org: bigint }`     → `org_scope=org` + `org_id={decimal-string}`
 */
export function serializeOrgScopeToQueryParams(
  scope: OrgScope | undefined
): Record<string, string> | undefined {
  if (scope === undefined) return undefined;
  if ('All' in scope) return undefined; // semantic All — omit the param
  if ('HelloWorld' in scope) return { org_scope: 'helloworld' };
  return { org_scope: 'org', org_id: scope.Org.toString() };
}
